import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    ShoppingBag,
    Clock,
    Target,
    ArrowRight,
    TrendingUp,
    Plus,
    Tag,
    X
} from 'lucide-react';
import { cn } from '../lib/utils';
import { StatusBadge } from '../components/ui/StatusBadge';

const GroupBuys: React.FC = () => {
    const [groupBuys, setGroupBuys] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isProposing, setIsProposing] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        goal_quantity: 0,
        unit_price: 0,
        discount_price: 0,
        deadline: ''
    });

    useEffect(() => { fetchGB(); }, []);

    const fetchGB = async () => {
        setLoading(true);
        const res = await api.get('/group-buys');
        setGroupBuys(res.data);
        setLoading(false);
    };

    const handleJoin = async (id: string) => {
        try {
            await api.post('/group-buys/join', { group_buy_id: id, quantity: 1 });
            alert('Interest registered!');
            fetchGB();
        } catch (e) { alert('Failed to join'); }
    };

    const handlePropose = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/group-buys/propose', formData);
            alert('Proposal submitted to the board for review.');
            setIsProposing(false);
            setFormData({ title: '', description: '', goal_quantity: 0, unit_price: 0, discount_price: 0, deadline: '' });
            fetchGB();
        } catch (e) { alert('Proposal failed'); }
    };

    return (
        <div className="space-y-8 max-w-4xl mx-auto pb-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Community Buys</h1>
                    <p className="text-slate-500 mt-1">Unlock collective discounts with your neighbors</p>
                </div>
                <button
                    onClick={() => setIsProposing(!isProposing)}
                    className="bg-violet-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-violet-700 transition-all flex items-center gap-2"
                >
                    {isProposing ? 'Cancel Proposal' : 'Propose a Deal'}
                    <Plus size={18} className={cn("transition-transform", isProposing && "rotate-45")} />
                </button>
            </div>

            {isProposing && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2">
                    <h3 className="font-bold text-slate-900 mb-4">Propose a Group Buy</h3>
                    <form onSubmit={handlePropose} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Deal Title</label>
                                <input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" placeholder="e.g. Organic Oranges" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Goal Qty</label>
                                <input type="number" value={formData.goal_quantity} onChange={e => setFormData({...formData, goal_quantity: parseInt(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Regular Price</label>
                                <input type="number" value={formData.unit_price} onChange={e => setFormData({...formData, unit_price: parseFloat(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">Group Price</label>
                                <input type="number" value={formData.discount_price} onChange={e => setFormData({...formData, discount_price: parseFloat(e.target.value)})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-500 uppercase">End Date</label>
                                <input type="date" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" required />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Description</label>
                            <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm" placeholder="Tell neighbors why this is a good deal..." />
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">Submit Proposal</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {groupBuys.map((gb: any) => {
                    const progress = Math.min((gb.current_quantity / gb.goal_quantity) * 100, 100);
                    const isGoalReached = progress >= 100;

                    return (
                        <div key={gb.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:border-violet-300 transition-all group">
                            <div className="p-6 flex-1">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                                        <ShoppingBag size={20} />
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <StatusBadge status={gb.status} />
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1">
                                            <Clock size={12} /> {new Date(gb.deadline).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>

                                <h2 className="text-lg font-bold text-slate-900 mb-2">{gb.title}</h2>
                                <p className="text-xs text-slate-500 line-clamp-2 mb-4">{gb.description}</p>

                                <div className="flex items-baseline gap-2 mb-6">
                                    <span className="text-2xl font-bold text-slate-900">₹{gb.discount_price}</span>
                                    <span className="text-sm text-slate-400 line-through">₹{gb.unit_price}</span>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        <span className="flex items-center gap-1"><Target size={12} /> {gb.current_quantity} / {gb.goal_quantity}</span>
                                        <span>{Math.round(progress)}%</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className={cn("h-full rounded-full transition-all duration-1000", isGoalReached ? "bg-emerald-500" : "bg-orange-500")}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => handleJoin(gb.id)}
                                disabled={gb.status !== 'active' || isGoalReached}
                                className={cn(
                                    "w-full py-4 flex items-center justify-center gap-2 font-bold text-sm transition-all",
                                    isGoalReached ? "bg-slate-50 text-slate-400 cursor-not-allowed" :
                                    gb.status === 'active' ? "bg-violet-600 text-white hover:bg-violet-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"
                                )}
                            >
                                {isGoalReached ? 'Goal Reached!' : gb.status === 'active' ? 'Register Interest' : 'Coming Soon'}
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GroupBuys;
